import { useEffect } from "react";
import { ctaEventForHref, linkLocation, localeFromPathname, outboundLinkInfo } from "@/lib/analytics/cta-events";
import { trackCta, trackOutboundClick } from "@/lib/analytics/track";

/**
 * Global click listener that fires a GA4 event whenever a visitor clicks a
 * link pointing at one of the tracked CTA destinations (request a demo,
 * contact us, explore the platform, external login).
 *
 * Listening at the document level means every CTA - hero buttons, nav items,
 * footer links, in-page cards - is covered without touching each component.
 */
export function CtaClickTracker() {
  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (event.defaultPrevented || event.button !== 0) return;
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute("href") ?? "";
      const pagePath = window.location.pathname;
      const name = ctaEventForHref(href);

      if (!name) {
        const outbound = outboundLinkInfo(href, window.location.hostname);
        if (outbound) {
          trackOutboundClick({
            link_url: outbound.url,
            link_domain: outbound.domain,
            link_text: (anchor.textContent ?? "").trim().slice(0, 120),
            link_location: linkLocation(anchor),
            page_path: pagePath,
            locale: localeFromPathname(pagePath),
          });
        }
        return;
      }

      trackCta(name, {
        link_text: (anchor.textContent ?? "").trim().slice(0, 120),
        link_url: anchor.href,
        page_path: pagePath,
        page_location: window.location.href,
        locale: localeFromPathname(pagePath),
        link_location: linkLocation(anchor),
        outbound: name === "cta_login",
      });
    }

    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);

  return null;
}
